import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
  ServerError,
  ServerParseError,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { useSagaAuth } from "@einride/saga-core"
import result from "gen/graphql"
import { TypedTypePolicies } from "gen/typePolicy"
import { ERROR_PERMISSION_DENIED } from "lib/api/context/errorCodes"
import { useConfig } from "lib/env"
import { useMemo } from "react"
import { useErrorBoundary } from "react-error-boundary"

const typePolicies: TypedTypePolicies = {
  FilterByRange: { keyFields: ["startMonth", "startYear"] },
  Dashboard: { keyFields: ["name"] },
  Datum: { keyFields: ["key", "values"] },
  FilterSelection: { keyFields: ["filterAttributeId"] },
  GroupedValue: { keyFields: false },
  Metric: { keyFields: ["metricId"] },
  MetricConfiguration: { keyFields: false },
  PageInfo: { keyFields: false },
  Report: { keyFields: ["name"] },
  SearchDashboardsResponse: { keyFields: false },
  QueryData: { keyFields: false },
  FilterTerm: { keyFields: ["id"] },
  FilterAttributeTerms: { keyFields: ["filterAttributeId"] },
  Date: { keyFields: false },
  DateTime: { keyFields: false },
  MetricDateSpan: { keyFields: false },
  SearchReportsResponse: { keyFields: false },
  SearchShipmentLegsResponse: { keyFields: false },
  ShipmentLeg: { keyFields: false },
  CountShipmentLegsResponse: { keyFields: false },
  GroupCount: { keyFields: false },
  TimestampOrDate: { keyFields: false },
  ComparedTo: { keyFields: false },
  Comparison: { keyFields: false },
  Query: {
    keyFields: false,
    fields: {
      SearchReports: {
        keyArgs: false,
        merge(existing, incoming) {
          return {
            ...incoming,
            reports: [...(existing?.reports ?? []), ...incoming.reports],
          }
        },
      },
    },
  },
}

const retryIf = (error: ServerError | ServerParseError): boolean => {
  const doNotRetryCodes = [400, 401, 403, 404, 500]
  return !!error && !doNotRetryCodes.includes(error.statusCode)
}

interface ApolloProviderContextProps {
  children: React.ReactNode
}

export const ApolloProviderContext = ({
  children,
}: ApolloProviderContextProps): React.JSX.Element => {
  const auth = useSagaAuth()
  const { showBoundary } = useErrorBoundary()
  const { environment, baseUrl } = useConfig()

  const authLink = useMemo((): ApolloLink => {
    return setContext(async (_req, { headers }) => {
      const token = await auth.user?.getIdToken()
      if (!token) {
        throw new Error("User token is not defined")
      }
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        },
      }
    })
  }, [auth.user])

  const client = useMemo(() => {
    const retryLink = new RetryLink({
      delay: {
        initial: 300,
        max: 2_000,
        jitter: true,
      },
      attempts: {
        max: 5,
        retryIf,
      },
    })

    const cacheConfig: InMemoryCacheConfig = {
      possibleTypes: result.possibleTypes,
      typePolicies,
    }

    const errorLink = onError(({ graphQLErrors, operation, forward }) => {
      if (graphQLErrors?.length) {
        // naively ignore all errors when the first one is handled
        // to not trigger different error handling mechanics for the same request
        const err = graphQLErrors[0]
        switch (err.extensions.code) {
          case ERROR_PERMISSION_DENIED:
            showBoundary(ERROR_PERMISSION_DENIED)
            break
          default:
            return forward(operation)
        }
      }
      return forward(operation)
    })

    const httpLink = new HttpLink({
      uri: `${baseUrl}/graphql`,
    })

    return new ApolloClient({
      link: ApolloLink.from([authLink, retryLink, errorLink, httpLink]),
      cache: new InMemoryCache(cacheConfig),
      connectToDevTools: environment !== "production",
    })
  }, [baseUrl, authLink, environment, showBoundary])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
